import React, { useMemo } from 'react';

import { green, blue } from '@mui/material/colors';
import { SxProps } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';

import AttachIcon from '@mui/icons-material/AttachFile';
import Delete from '@mui/icons-material/Delete';

import Typography from '@ts-core/components/Typography';
import ConfirmButton from '@ts-core/components/ConfirmButton';
import RichHtmlText from '@ts-core/core/components/RichHtmlText';

import { formatDateTime } from '@ts-core/core/utils/dateFormatters';

interface ActivityBaseMessageProps {
  sx?: SxProps;
  title?: React.ReactNode;

  loading?: boolean;
  mode: 'professor' | 'student';

  text?: string;
  file?: {
    name: string;
    url: string;
  };
  date?: string;

  onDelete?: () => void;
  deleteAlertMessage?: string;
}

const ActivityBaseMessage = (props: ActivityBaseMessageProps) => {
  const { sx = [], title, text, mode, file, date, loading, onDelete, deleteAlertMessage } = props;

  const bgColor = useMemo(() => {
    if (mode === 'professor') {
      return green[50];
    }
    return blue[50];
  }, [mode]);

  const titleColor = useMemo(() => {
    if (mode === 'professor') {
      return green[900];
    }
    return blue[900];
  }, [mode]);

  return (
    <Box
      width="100%"
      border="1px solid rgba(0, 0, 0, 0.2)"
      overflow="hidden"
      borderRadius={4}
      px={2}
      pt={2}
      pb={1}
      boxShadow={2}
      sx={[
        {
          backgroundColor: bgColor,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {title && (
        <Typography variant="body1" color={titleColor} fontWeight={700} loading={loading}>
          {title}
        </Typography>
      )}
      <Box display="flex" flexDirection="row" alignItems="center">
        <Stack width="100%">
          {text && <RichHtmlText content={text} loading={loading} />}
          {!!file && (
            <Link sx={{ display: 'flex', mt: !text ? 2 : 0 }} href={file.url} target="__blank" download>
              <AttachIcon />
              {file.name}
            </Link>
          )}
        </Stack>
        {onDelete && (
          <Box>
            <ConfirmButton
              onConfirm={onDelete}
              alertMessage={deleteAlertMessage}
              renderButton={({ onClick }) => (
                <IconButton onClick={onClick}>
                  <Delete />
                </IconButton>
              )}
            />
          </Box>
        )}
      </Box>
      {date && (
        <Typography textAlign="right" fontSize="caption.fontSize" color="caption.fontColor">
          {formatDateTime(date)}
        </Typography>
      )}
    </Box>
  );
};

export default ActivityBaseMessage;
